import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeading,
  StatusBadge,
} from "@/components/app/AppUI";
import {
  clientRepository,
  eventRepository,
  leadRepository,
  paymentRepository,
  quoteRepository,
} from "@/data/repositories";
import {
  ETIQUETA_ESTADO_BODA,
  ETIQUETA_FILTRO,
  fechaDeLaBoda,
  filasClientes,
  nombreDeLaPareja,
  pasaFiltro,
  type FiltroBodas,
} from "@/domain/bodas";
import { resumenGlobal, textoImporte } from "@/domain/cobros";

export const Route = createFileRoute("/_authenticated/app/clientes")({ component: ClientesLayout });

function ClientesLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (/\/app\/clientes\/.+/.test(pathname)) return <Outlet />;
  return <ClientesPage />;
}

const FILTROS: readonly FiltroBodas[] = ["proximas", "celebradas", "todas"];

/** Las parejas que han reservado, con su boda y lo que queda por cobrar. */
function ClientesPage() {
  const navigate = useNavigate();
  const clientes = useQuery({ queryKey: ["clients"], queryFn: () => clientRepository.list() });
  const bodas = useQuery({ queryKey: ["events"], queryFn: () => eventRepository.list() });
  const presupuestos = useQuery({ queryKey: ["quotes"], queryFn: () => quoteRepository.list() });
  const pagos = useQuery({ queryKey: ["payments"], queryFn: () => paymentRepository.list() });
  const leads = useQuery({ queryKey: ["leads"], queryFn: () => leadRepository.list() });
  const [busqueda, setBusqueda] = useState("");
  const [filtro, setFiltro] = useState<FiltroBodas>("proximas");
  const [soloPendientes, setSoloPendientes] = useState(false);

  const nombresLead = useMemo(
    () =>
      new Map(
        (leads.data ?? [])
          .filter((lead) => lead.converted_client_id)
          .map((lead) => [lead.converted_client_id as string, lead.name]),
      ),
    [leads.data],
  );

  const filas = useMemo(() => {
    const ahora = new Date();
    const texto = busqueda.trim().toLowerCase();
    return filasClientes(
      clientes.data ?? [],
      bodas.data ?? [],
      presupuestos.data ?? [],
      pagos.data ?? [],
      ahora,
    )
      .map((fila) => ({
        ...fila,
        nombre: nombreDeLaPareja(fila.cliente, nombresLead.get(fila.cliente.id)),
      }))
      .filter(
        (fila) =>
          pasaFiltro(fila, filtro, soloPendientes, ahora) &&
          `${fila.nombre} ${fila.cliente.email ?? ""} ${fila.cliente.phone ?? ""} ${fila.boda?.venue ?? ""}`
            .toLowerCase()
            .includes(texto),
      )
      .sort(
        (a, b) =>
          (a.boda?.starts_at ? Date.parse(a.boda.starts_at) : Infinity) -
          (b.boda?.starts_at ? Date.parse(b.boda.starts_at) : Infinity),
      );
  }, [
    clientes.data,
    bodas.data,
    presupuestos.data,
    pagos.data,
    nombresLead,
    busqueda,
    filtro,
    soloPendientes,
  ]);

  const consultas = [clientes, bodas, presupuestos, pagos, leads];
  if (consultas.some((c) => c.isLoading)) return <LoadingState />;
  const error = consultas.find((c) => c.error)?.error;
  if (error) return <ErrorState message={error.message} />;

  // Totales de los clientes activos, sin bodas canceladas.
  const activos = new Set((clientes.data ?? []).map((c) => c.id));
  const bodasActivas = new Set(
    (bodas.data ?? [])
      .filter((b) => b.client_id && activos.has(b.client_id) && b.status !== "CANCELLED")
      .map((b) => b.id),
  );
  const totales = resumenGlobal(
    (presupuestos.data ?? []).filter((p) => bodasActivas.has(p.event_id)),
    (pagos.data ?? []).filter((p) => bodasActivas.has(p.event_id)),
  );

  return (
    <div className="space-y-7">
      <PageHeading
        eyebrow="Bodas"
        title="Clientes"
        description="Cada pareja que ha reservado, con su boda y lo que queda por cobrar."
      />

      <section className="grid gap-px border border-border bg-border sm:grid-cols-3">
        <Total etiqueta="Contratado" valor={textoImporte(totales.total ?? 0)} />
        <Total etiqueta="Cobrado" valor={textoImporte(totales.pagado)} />
        <Total etiqueta="Pendiente" valor={textoImporte(totales.pendiente)} destacado />
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-60 max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="field pl-10"
            placeholder="Buscar por nombre, teléfono, email o lugar"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filtrar bodas">
          {FILTROS.map((f) => (
            <Chip key={f} activo={filtro === f} alPulsar={() => setFiltro(f)}>
              {ETIQUETA_FILTRO[f]}
            </Chip>
          ))}
          <Chip activo={soloPendientes} alPulsar={() => setSoloPendientes(!soloPendientes)}>
            Con pagos pendientes
          </Chip>
        </div>
      </div>

      <section className="panel">
        <div className="data-row hidden grid-cols-[1.4fr_8rem_1fr_8rem_7rem] font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground md:grid">
          <span>Pareja</span>
          <span>Boda</span>
          <span>Lugar</span>
          <span>Estado</span>
          <span className="text-right">Pendiente</span>
        </div>
        {filas.length === 0 ? (
          <EmptyState
            title="Nadie por aquí"
            description="Las parejas aparecen aquí cuando reservas un lead."
          />
        ) : (
          filas.map((fila) => (
            <button
              key={fila.cliente.id}
              className="data-row w-full grid-cols-[1fr_auto] text-left md:grid-cols-[1.4fr_8rem_1fr_8rem_7rem]"
              onClick={() => navigate({ to: "/app/clientes/$id", params: { id: fila.cliente.id } })}
            >
              <div>
                <p className="font-semibold">{fila.nombre}</p>
                <p className="text-sm text-muted-foreground md:hidden">
                  {fechaDeLaBoda(fila.boda?.starts_at ?? null)}
                </p>
              </div>
              <p className="hidden md:block">{fechaDeLaBoda(fila.boda?.starts_at ?? null)}</p>
              <p className="hidden truncate md:block">{fila.boda?.venue ?? "Sin definir"}</p>
              <div className="hidden md:block">
                {fila.boda ? (
                  <StatusBadge
                    status={fila.boda.status}
                    label={ETIQUETA_ESTADO_BODA[fila.boda.status]}
                  />
                ) : (
                  <span className="text-sm text-muted-foreground">Sin boda</span>
                )}
              </div>
              <p
                className={`text-right font-semibold ${fila.cobros.pendiente > 0 ? "" : "text-muted-foreground"}`}
              >
                {textoImporte(fila.cobros.pendiente)}
              </p>
            </button>
          ))
        )}
      </section>
    </div>
  );
}

function Total({
  etiqueta,
  valor,
  destacado = false,
}: {
  etiqueta: string;
  valor: string;
  destacado?: boolean;
}) {
  return (
    <div className={destacado ? "bg-onyx px-5 py-4 text-bone" : "bg-white px-5 py-4"}>
      <p className={`eyebrow ${destacado ? "!text-neon" : ""}`}>{etiqueta}</p>
      <p className="mt-1 font-display text-3xl tracking-[-0.04em]">{valor}</p>
    </div>
  );
}

function Chip({
  activo,
  alPulsar,
  children,
}: {
  activo: boolean;
  alPulsar: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={activo}
      onClick={alPulsar}
      className={`border px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors ${
        activo
          ? "border-foreground bg-foreground text-background"
          : "border-border bg-white text-muted-foreground hover:border-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
